/**
 * First-visit guide — auto-opens for the first 3 browser sessions.
 * Minimizes to a bottom-right chip on all pages until the user taps Got it.
 */

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';

const VIEWS_KEY = 'transport-team-how-it-works-views';
const DISMISSED_KEY = 'transport-team-how-it-works-dismissed';
const SESSION_KEY = 'transport-team-how-it-works-session';
const SESSION_SHOWN_KEY = 'transport-team-how-it-works-session-shown';
const MAX_AUTO_VIEWS = 3;

const accent = '#0d7377';
const card = '#1a2332';
const text = '#e8eaed';
const muted = '#9aa0a6';

const sections = [
  {
    id: 'team',
    label: 'Organisation transport',
    emoji: '🚌',
    intro:
      'For schools, churches, sports teams, and workplaces — coordinate buses, daily attendance, and live ETAs.',
    roles: [
      {
        title: 'Organisation admin',
        emoji: '📋',
        steps: [
          'Register your organisation (name, contact, city, and how you use transport).',
          'Add buses with type and seat capacity — the app matches buses to areas based on passenger demand.',
          'Approve driver registrations and review uploaded licence files.',
          'Use the Assignments tab to rearrange drivers, areas, and passenger buses after auto-assign.',
          'See passengers grouped by area and whether drivers started on time.',
        ],
      },
      {
        title: 'Driver',
        emoji: '🚌',
        steps: [
          'Register with your organisation invite link using ?role=driver, select licence type, and upload your licence at registration.',
          'Wait for admin approval, then sign in and go online — the app auto-assigns you to a matching bus.',
          'Start your route before notifying passengers or setting ETA while en route.',
          'Mark pickups, then tap Route done (or Release bus when there is no waitlist).',
        ],
      },
      {
        title: 'Passenger / commuter',
        emoji: '👤',
        steps: [
          'Register via QR or invite code with your pickup area and age range.',
          'Each day, choose how many people are going (everyone uses a seat) and toggle going.',
          'The app assigns you to the best-fit bus for your area — waitlisted if full.',
          'View scheduled departure and live ETA when confirmed on a bus.',
        ],
      },
    ],
  },
];

function readViewCount() {
  try {
    const n = parseInt(localStorage.getItem(VIEWS_KEY) || '0', 10);
    return Number.isNaN(n) ? 0 : Math.min(Math.max(n, 0), MAX_AUTO_VIEWS);
  } catch {
    return 0;
  }
}

function writeViewCount(n) {
  try {
    localStorage.setItem(VIEWS_KEY, String(Math.min(Math.max(n, 0), MAX_AUTO_VIEWS)));
  } catch {
    /* ignore */
  }
}

function readDismissed() {
  try {
    return localStorage.getItem(DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
}

function writeDismissed() {
  try {
    localStorage.setItem(DISMISSED_KEY, '1');
  } catch {
    /* ignore */
  }
}

function ensureSessionId() {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = String(Date.now());
      sessionStorage.setItem(SESSION_KEY, id);
      sessionStorage.removeItem(SESSION_SHOWN_KEY);
    }
    return id;
  } catch {
    return 'default';
  }
}

function hasAutoShownThisSession() {
  try {
    const sessionId = sessionStorage.getItem(SESSION_KEY);
    return sessionId && sessionStorage.getItem(SESSION_SHOWN_KEY) === sessionId;
  } catch {
    return false;
  }
}

function markAutoShownThisSession() {
  try {
    const sessionId = ensureSessionId();
    sessionStorage.setItem(SESSION_SHOWN_KEY, sessionId);
  } catch {
    /* ignore */
  }
}

export default function HowItWorksGuide() {
  const [viewCount, setViewCount] = useState(readViewCount);
  const [dismissed, setDismissed] = useState(readDismissed);
  const [expanded, setExpanded] = useState(false);
  const [ready, setReady] = useState(false);

  const canAutoShow = viewCount < MAX_AUTO_VIEWS;
  const showChip = !dismissed && !expanded;

  useEffect(() => {
    setReady(true);
    if (readDismissed()) return;
    if (readViewCount() >= MAX_AUTO_VIEWS) return;
    if (hasAutoShownThisSession()) return;

    markAutoShownThisSession();
    const next = readViewCount() + 1;
    writeViewCount(next);
    setViewCount(next);
    setExpanded(true);
  }, []);

  function handleMinimize() {
    setExpanded(false);
  }

  function handleExpand() {
    setExpanded(true);
  }

  function handleDismiss() {
    setExpanded(false);
    setDismissed(true);
    writeDismissed();
    writeViewCount(MAX_AUTO_VIEWS);
    setViewCount(MAX_AUTO_VIEWS);
  }

  if (!ready || dismissed) return null;

  const modal = expanded ? (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="how-it-works-title"
      style={overlayStyle}
      onClick={handleMinimize}
    >
      <div style={panelStyle} onClick={(e) => e.stopPropagation()}>
        <header style={panelHeaderStyle}>
          <div>
            <p style={{ margin: 0, fontSize: '0.8rem', color: accent, fontWeight: 700, letterSpacing: '0.04em' }}>
              HOW IT WORKS
            </p>
            <h2 id="how-it-works-title" style={{ margin: '6px 0 0', fontSize: '1.35rem', color: text }}>
              How Transport Team works
            </h2>
            <p style={{ margin: '8px 0 0', color: muted, fontSize: '0.88rem', lineHeight: 1.5 }}>
              Organisation bus coordination for admins, drivers, and commuters.
            </p>
          </div>
          <button type="button" onClick={handleMinimize} style={iconBtnStyle} aria-label="Minimize">
            −
          </button>
        </header>

        <div style={quickLinksStyle}>
          <Link to="/org/register" style={quickLinkStyle} onClick={(e) => e.stopPropagation()}>
            Register organisation
          </Link>
          <Link to="/login" style={quickLinkMutedStyle} onClick={(e) => e.stopPropagation()}>
            Sign in
          </Link>
        </div>

        <div style={bodyStyle}>
          {sections.map((section) => (
            <div key={section.id} style={sectionBlockStyle}>
              <h3 style={sectionHeadingStyle}>
                <span aria-hidden="true">{section.emoji} </span>
                {section.label}
              </h3>
              <p style={sectionIntroStyle}>{section.intro}</p>
              {section.roles.map((role) => (
                <section key={role.title} style={roleSectionStyle}>
                  <h4 style={{ margin: '0 0 10px', fontSize: '0.95rem', color: text, fontWeight: 600 }}>
                    <span aria-hidden="true">{role.emoji} </span>
                    {role.title}
                  </h4>
                  <ol style={{ margin: 0, paddingLeft: 20, color: muted, fontSize: '0.86rem', lineHeight: 1.55 }}>
                    {role.steps.map((step) => (
                      <li key={step} style={{ marginBottom: 6 }}>
                        {step}
                      </li>
                    ))}
                  </ol>
                </section>
              ))}
            </div>
          ))}
        </div>

        <footer style={footerStyle}>
          {canAutoShow ? (
            <p style={{ margin: '0 0 12px', fontSize: '0.8rem', color: muted }}>
              Tip {viewCount} of {MAX_AUTO_VIEWS} — opens automatically on your next visit.
            </p>
          ) : null}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            <button type="button" onClick={handleMinimize} style={secondaryBtnStyle}>
              Minimize
            </button>
            <button type="button" onClick={handleDismiss} style={primaryBtnStyle}>
              Got it, don&apos;t show again
            </button>
          </div>
        </footer>
      </div>
    </div>
  ) : null;

  return (
    <>
      {modal ? createPortal(modal, document.body) : null}

      {showChip ? (
        <button
          type="button"
          onClick={handleExpand}
          className="how-it-works-chip"
          aria-label="Open how Transport Team works"
        >
          <span aria-hidden="true">🚌</span>
          <span>How it works</span>
        </button>
      ) : null}
    </>
  );
}

const overlayStyle = {
  position: 'fixed',
  inset: 0,
  zIndex: 2147483000,
  background: 'rgba(0,0,0,0.72)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 16,
  boxSizing: 'border-box',
};

const panelStyle = {
  width: '100%',
  maxWidth: 560,
  maxHeight: 'min(92vh, 720px)',
  background: card,
  borderRadius: 16,
  border: '1px solid rgba(255,255,255,0.08)',
  boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const panelHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: 12,
  padding: '20px 20px 12px',
  borderBottom: '1px solid rgba(255,255,255,0.06)',
};

const quickLinksStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
  padding: '12px 20px',
  borderBottom: '1px solid rgba(255,255,255,0.06)',
};

const quickLinkStyle = {
  fontSize: '0.8rem',
  fontWeight: 600,
  color: '#fff',
  textDecoration: 'none',
  padding: '8px 12px',
  borderRadius: 8,
  background: `linear-gradient(180deg, ${accent}, #0a5c5f)`,
};

const quickLinkMutedStyle = {
  fontSize: '0.8rem',
  fontWeight: 600,
  color: text,
  textDecoration: 'none',
  padding: '8px 12px',
  borderRadius: 8,
  border: '1px solid rgba(255,255,255,0.12)',
};

const bodyStyle = {
  flex: 1,
  overflowY: 'auto',
  padding: '16px 20px',
};

const sectionBlockStyle = {
  marginBottom: 22,
};

const sectionHeadingStyle = {
  margin: '0 0 6px',
  fontSize: '1.05rem',
  color: text,
  fontWeight: 700,
};

const sectionIntroStyle = {
  margin: '0 0 14px',
  fontSize: '0.86rem',
  color: muted,
  lineHeight: 1.5,
};

const roleSectionStyle = {
  marginBottom: 12,
  padding: 12,
  borderRadius: 12,
  background: 'rgba(0,0,0,0.2)',
  border: '1px solid rgba(255,255,255,0.05)',
};

const footerStyle = {
  padding: '14px 20px 18px',
  borderTop: '1px solid rgba(255,255,255,0.06)',
};

const iconBtnStyle = {
  flexShrink: 0,
  width: 36,
  height: 36,
  borderRadius: 10,
  border: '1px solid rgba(255,255,255,0.12)',
  background: 'rgba(0,0,0,0.25)',
  color: text,
  fontSize: '1.25rem',
  lineHeight: 1,
  cursor: 'pointer',
};

const primaryBtnStyle = {
  flex: '1 1 140px',
  padding: '12px 16px',
  borderRadius: 10,
  border: 'none',
  background: `linear-gradient(180deg, ${accent}, #0a5c5f)`,
  color: '#fff',
  fontWeight: 600,
  cursor: 'pointer',
  fontSize: '0.95rem',
};

const secondaryBtnStyle = {
  flex: '1 1 120px',
  padding: '12px 16px',
  borderRadius: 10,
  border: '1px solid rgba(255,255,255,0.15)',
  background: 'transparent',
  color: text,
  fontWeight: 600,
  cursor: 'pointer',
  fontSize: '0.95rem',
};
